import { Button, Form, InputV2, PlayButton, Text } from "@southfields-digital/mxmz-react-ui";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import useKeypress from "src/hooks/useKeypress";
import {
	REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
	REQUEST_ANIMATE_IN_PREVIEW_GRAPHIC,
	REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
	REQUEST_ANIMATE_OUT_PREVIEW_GRAPHIC,
	REQUEST_UPDATE_LIVE_GRAPHIC,
	REQUEST_UPDATE_PREVIEW_GRAPHIC,
} from "src/redux/reducers/control";
import { REQUEST_UPDATE_PLAYLIST_GRAPHIC } from "src/redux/reducers/playlistGraphic";

import styles from "./GraphicControlForm.module.scss";
import { isSwappable } from "./utils";

interface IGraphicControlFormProps {
	selectedGraphic: PlaylistGraphic;
	showStatsPerform?: () => void;
	showDataConnector?: () => void;
	isAutoUpdateEnabled?: boolean;
}

export default function GraphicControlForm({
	selectedGraphic,
	showStatsPerform,
	showDataConnector,
	isAutoUpdateEnabled = false,
}: IGraphicControlFormProps) {
	const dispatch = useDispatch();
	const [name, setName] = useState<string>("");
	const [editable, setEditable] = useState<boolean>(false);

	const inputRef = useRef<HTMLInputElement>(null);
	const selectOnFocusRef = useRef(false);

	useEffect(() => {
		setName(selectedGraphic?.name || "");
	}, [selectedGraphic]);

	const updateGraphicProperties = () => {
		if (!selectedGraphic || selectedGraphic.name === name) return;

		dispatch({
			type: REQUEST_UPDATE_PLAYLIST_GRAPHIC,
			payload: { graphic: { ...selectedGraphic, name } },
		});
	};

	const handleSubmitForm = () => {
		updateGraphicProperties();
		setEditable(false);
	};

	const previewIn = () => {
		if (!selectedGraphic) return;

		dispatch({
			type: REQUEST_ANIMATE_IN_PREVIEW_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id },
		});
	};

	const previewOut = () => {
		if (!selectedGraphic) return;

		dispatch({
			type: REQUEST_ANIMATE_OUT_PREVIEW_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id },
		});
	};

	const handleTogglePreview = () => {
		if (!selectedGraphic) return;

		if (selectedGraphic.status.preview) previewOut();
		else previewIn();
	};

	useKeypress("F3", {}, () => {
		previewIn();
	});

	useKeypress("F4", {}, () => {
		previewOut();
	});

	const liveIn = () => {
		if (!selectedGraphic) return;

		dispatch({
			type: REQUEST_ANIMATE_IN_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id },
		});
	};

	const liveOut = () => {
		if (!selectedGraphic) return;

		dispatch({
			type: REQUEST_ANIMATE_OUT_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id },
		});
	};

	const fade = () => {
		dispatch({
			type: REQUEST_UPDATE_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id, payload: "swap:fade" },
		});
	};

	const cut = () => {
		dispatch({
			type: REQUEST_UPDATE_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id, payload: "swap:cut" },
		});
	};

	const tickNext = (preview = false) => {
		if (!selectedGraphic || !selectedGraphic.graphic.config.isTickable) return;
		dispatch({
			type: preview ? REQUEST_UPDATE_PREVIEW_GRAPHIC : REQUEST_UPDATE_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id, payload: "tick:next" },
		});
	};

	const tickPrev = (preview?: boolean) => {
		if (!selectedGraphic || !selectedGraphic.graphic.config.isTickable) return;
		dispatch({
			type: preview ? REQUEST_UPDATE_PREVIEW_GRAPHIC : REQUEST_UPDATE_LIVE_GRAPHIC,
			payload: { playlistGraphicId: selectedGraphic.id, payload: "tick:previous" },
		});
	};

	useKeypress("F1", {}, () => {
		if (!selectedGraphic || (selectedGraphic.status.live && !isSwappable(selectedGraphic))) return;
		liveIn();
	});

	useKeypress("F2", {}, () => {
		if (!selectedGraphic || !selectedGraphic.status.live) return;
		liveOut();
	});

	const handleToggleLive = () => {
		if (!selectedGraphic) return;

		if (selectedGraphic.status.live) {
			liveOut();
		} else {
			liveIn();
		}
	};

	const handleClickCut = () => {
		if (!selectedGraphic || !isSwappable(selectedGraphic)) return;
		cut();
	};

	const handleClickFade = () => {
		if (!selectedGraphic || !isSwappable(selectedGraphic)) return;
		fade();
	};

	useEffect(() => {
		if (editable) {
			// Focus the input whenever edit mode is activated
			inputRef.current?.focus();

			// Select all text when double clicking on the text
			if (selectOnFocusRef.current) {
				inputRef.current?.select();
				selectOnFocusRef.current = false;
			}
		}
	}, [editable]);

	const handleClickEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		setEditable(true);
	};

	const handleDoubleClickText = () => {
		// Set the flag to select all text when the input is focused
		selectOnFocusRef.current = true;
		setEditable(true);
	};

	const handleClickSave = () => {
		updateGraphicProperties();
		setEditable(false);
	};

	const handleClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
		if (e.detail === 2) {
			setEditable(true);
			inputRef.current?.focus();
		}
	};

	const handleBlurInput = () => {
		updateGraphicProperties();
		setEditable(false);
	};

	if (!selectedGraphic) return null;
	return (
		<div className={styles.Container}>
			<Form onSubmit={handleSubmitForm} className={styles.Form}>
				<Button
					as="button"
					variant="tertiary"
					iconProps={{
						icon: editable ? "check" : "pencil",
						size: 16,
						color: "#5B5D70",
						weight: "bold",
					}}
					onClick={editable ? handleClickSave : handleClickEdit}
					className={styles.EditButton}
				/>
				{editable ? (
					<InputV2
						name="name"
						ref={inputRef}
						value={name}
						data-1p-ignore
						className="!text-base"
						wrapperClassName="max-w-fit"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.currentTarget.value)}
						onClick={handleClickInput}
						onBlur={handleBlurInput}
					/>
				) : (
					<div onDoubleClick={handleDoubleClickText} className={styles.TextContainer}>
						<Text>{name}</Text>
					</div>
				)}
			</Form>
			<div className={styles.Controls}>
				{showStatsPerform && (
					<PlayButton
						hasIcon={false}
						isRunning={false}
						variant="live"
						onClick={showStatsPerform}
						title="Show sport data"
					>
						Sport Data
					</PlayButton>
				)}
				{showDataConnector && (
					<PlayButton
						hasIcon
						isRunning={isAutoUpdateEnabled}
						variant="fetchData"
						onClick={showDataConnector}
						iconProps={{ icon: "database" }}
						title="Open data connector"
					/>
				)}
				<PlayButton
					hasIcon
					variant="preview"
					onClick={handleTogglePreview}
					isRunning={selectedGraphic.status.preview}
					iconProps={{ icon: "eye" }}
					title={selectedGraphic.status.preview ? "Hide preview (F4)" : "Show preview (F3)"}
				/>
				{selectedGraphic.graphic.config.isTickable && (
					<>
						<PlayButton
							variant="tickPrev"
							hasIcon
							isRunning={selectedGraphic.status.preview}
							onClick={() => tickPrev(true)}
							title="Previous tick (preview)"
						/>
						<PlayButton
							variant="tickNext"
							hasIcon
							isRunning={selectedGraphic.status.preview}
							onClick={() => tickNext(true)}
							title="Next tick (preview)"
						/>
					</>
				)}
				{selectedGraphic.graphic.config.isSwappable && (
					<div className={styles.SwappableContainer}>
						<PlayButton
							hasIcon
							iconPosition="right"
							isRunning={false}
							variant="fade"
							onClick={handleClickFade}
							title="Fade transition"
						>
							Fade
						</PlayButton>
						<PlayButton
							hasIcon
							iconPosition="right"
							isRunning={false}
							variant="cut"
							onClick={handleClickCut}
							title="Cut transition"
						>
							Cut
						</PlayButton>
					</div>
				)}
				<div className={styles.LiveControls}>
					<PlayButton
						hasIcon
						variant="live"
						onClick={handleToggleLive}
						isRunning={selectedGraphic.status.live}
						title={selectedGraphic.status.live ? "Take out (F2)" : "Take in (F1)"}
					>
						Take
					</PlayButton>
					{selectedGraphic.graphic.config.isTickable && (
						<>
							<PlayButton
								variant="tickPrev"
								hasIcon
								isRunning={selectedGraphic.status.live}
								onClick={() => tickPrev()}
								title="Previous tick (live)"
							/>
							<PlayButton
								variant="tickNext"
								hasIcon
								isRunning={selectedGraphic.status.live}
								onClick={() => tickNext()}
								title="Next tick (live)"
							/>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
